// W2Cook Billing Dashboard — Configure / Add connector modals

import { useState } from "react";
import type { ConnectorTemplate } from "../connectors/templates";
import { auth } from "../auth/firebase";
import { tokens as T } from "../theme/tokens";
import { Btn, Field, Modal } from "./ui";

// ── Configure existing connector ──────────────────────────────────────────────
interface ConfigProps {
  template: ConnectorTemplate;
  existing: Record<string, string> | undefined;
  onSave:   (cfg: Record<string, string>) => void;
  onRemove: () => void;
  onClose:  () => void;
}

export function ConnectorModal({ template, existing, onSave, onRemove, onClose }: ConfigProps) {
  const [cfg, setCfg] = useState<Record<string, string>>(existing ?? {});
  const [status, setStatus] = useState<{ ok: boolean; message: string } | null>(null);
  const [testing, setTesting] = useState<boolean>(false);

  const testConnection = async () => {
    setTesting(true);
    setStatus(null);
    try {
      const url     = template.buildEndpoint(cfg);
      const headers = template.buildHeaders(cfg);

      // Mirror the polling path: direct when the vendor supports CORS,
      // otherwise relay through the Cloud Run proxy.
      let res: Response;
      if (template.corsOk) {
        res = await fetch(url, { headers });
      } else {
        const proxy = import.meta.env.VITE_PROXY_URL ?? "";
        if (!proxy) {
          setStatus({ ok: false, message: "This connector needs the API proxy. Set VITE_PROXY_URL." });
          setTesting(false);
          return;
        }
        const idToken = auth ? await auth.currentUser?.getIdToken() : undefined;
        if (!idToken) {
          setStatus({ ok: false, message: "Not signed in — cannot authenticate to proxy." });
          setTesting(false);
          return;
        }
        res = await fetch(`${proxy}/proxy`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ url, headers, method: "GET" }),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const parsed = template.parseResult(data, cfg);
        setStatus({ ok: true, message: parsed.note || "Connection successful" });
      } else {
        const errMsg = (data as any)?.message ?? (data as any)?.error ?? "Request failed";
        setStatus({ ok: false, message: `HTTP ${res.status}: ${errMsg}` });
      }
    } catch (e: any) {
      setStatus({ ok: false, message: `Network error: ${e?.message ?? "unknown"}` });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal title={`${template.icon} ${template.name}`} onClose={onClose}>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 18 }}>
        Credentials are stored in browser localStorage (dev) or Firestore (prod) — never sent to third parties.{" "}
        <a href={template.docsUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.blue }}>API docs ↗</a>
      </div>

      {template.fields.map((f) => (
        <Field
          key={f.key}
          label={f.label}
          value={cfg[f.key] ?? ""}
          onChange={(v) => setCfg((p: Record<string, string>) => ({ ...p, [f.key]: v }))}
          placeholder={f.placeholder}
          secret={f.secret}
        />
      ))}

      {status && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 14,
          background: status.ok ? `${T.sage}15` : `${T.ember}15`,
          border: `1px solid ${status.ok ? T.sage : T.ember}40`,
          color: status.ok ? T.sage : T.ember, fontSize: 13,
        }}>
          {status.ok ? "✅" : "❌"} {status.message}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {existing && <Btn variant="danger" onClick={onRemove}>Remove</Btn>}
        <Btn variant="ghost" onClick={testConnection} style={{ flex: 1 }} disabled={testing}>
          {testing ? "Testing…" : "Test Connection"}
        </Btn>
        <Btn variant="primary" onClick={() => onSave(cfg)} style={{ flex: 1 }}>
          Save Credentials
        </Btn>
      </div>
    </Modal>
  );
}

// ── Add a brand-new connector template ────────────────────────────────────────
interface AddProps {
  onSave:  (tpl: ConnectorTemplate) => void;
  onClose: () => void;
}

export function AddConnectorModal({ onSave, onClose }: AddProps) {
  const [form, setForm] = useState({
    id:          "",
    name:        "",
    icon:        "🔌",
    docsUrl:     "",
    endpointTpl: "",
    authHeader:  "Bearer",
    extraField:  "",    // optional second field label
    parseField:  "",    // top-level JSON key to read as amount
  });
  const set = (k: string, v: string) => setForm((p: typeof form) => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (!form.id || !form.name || !form.endpointTpl) {
      alert("ID, Name and Endpoint URL are required.");
      return;
    }

    const fields = [
      { key: "token", label: "API Token / Key", placeholder: "Enter token", secret: true },
      ...(form.endpointTpl.includes("{accountId}")
        ? [{ key: "accountId", label: "Account ID", placeholder: "xxx", secret: false }]
        : []),
      ...(form.extraField
        ? [{ key: "extraField", label: form.extraField, placeholder: "", secret: false }]
        : []),
    ];

    const tpl: ConnectorTemplate = {
      id:       form.id.toLowerCase().replace(/\s+/g, "-"),
      name:     form.name,
      icon:     form.icon,
      docsUrl:  form.docsUrl,
      _custom:  true,
      fields,
      buildEndpoint: (cfg) =>
        form.endpointTpl
          .replace("{token}",     cfg.token ?? "")
          .replace("{accountId}", cfg.accountId ?? ""),
      buildHeaders: (cfg) => ({
        [form.authHeader === "Bearer" ? "Authorization" : form.authHeader]:
          form.authHeader === "Bearer" ? `Bearer ${cfg.token}` : cfg.token,
      }),
      parseResult: (data) => ({
        ok:     true,
        amount: form.parseField ? (data as any)?.[form.parseField] ?? null : null,
        note:   `${form.name} data fetched`,
        raw:    data,
      }),
    };

    onSave(tpl);
  };

  return (
    <Modal title="Add API Connector" onClose={onClose} width={480}>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 18 }}>
        Define a new API integration. This creates a reusable connector template persisted to storage.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "72px 1fr", gap: "0 12px" }}>
        <Field label="ICON" value={form.icon} onChange={(v) => set("icon", v)} placeholder="🔌" />
        <Field label="CONNECTOR NAME" value={form.name} onChange={(v) => set("name", v)} placeholder="e.g. Stripe" />
      </div>
      <Field label="CONNECTOR ID (unique, lowercase, no spaces)" value={form.id} onChange={(v) => set("id", v)} placeholder="stripe" />
      <Field label="DOCS URL" value={form.docsUrl} onChange={(v) => set("docsUrl", v)} placeholder="https://stripe.com/docs/api" />
      <Field
        label="ENDPOINT URL"
        value={form.endpointTpl}
        onChange={(v) => set("endpointTpl", v)}
        placeholder="https://api.stripe.com/v1/balance"
      />
      <div style={{ fontSize: 11, color: T.textDim, marginTop: -10, marginBottom: 14 }}>
        Use <code style={{ color: T.gold }}>{"{accountId}"}</code> as a placeholder if the endpoint needs an account/org ID.
      </div>
      <Field label="AUTH HEADER TYPE" value={form.authHeader} onChange={(v) => set("authHeader", v)} placeholder="Bearer" />
      <Field
        label="EXTRA FIELD LABEL (optional — e.g. Org Name)"
        value={form.extraField}
        onChange={(v) => set("extraField", v)}
        placeholder=""
      />
      <Field
        label="RESPONSE KEY TO USE AS COST (optional)"
        value={form.parseField}
        onChange={(v) => set("parseField", v)}
        placeholder="e.g. amount, balance, total"
      />

      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <Btn variant="ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSave} style={{ flex: 1 }}>Add Connector</Btn>
      </div>
    </Modal>
  );
}
